import { TOOLBAR_BUTTON_SX } from "@/controls/buttons/toolbarButton";
import { ResetFiltersButton } from "@/controls/buttons/ResetFiltersButton";
import { ErrorModal, useContextMenuMESF } from "@dexteel/mesf-core";
import Typography from "@mui/material/Typography";
import {
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import {
	SHOW_ONLY_ACTIVES_DEFAULT,
	ShowOnlyActivesCheckbox,
} from "@/controls/filters/ShowOnlyActivesCheckbox";
import { Box, Button, Paper } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { urlParam, useUrlState } from "@/shared/hooks/useUrlState";
import { useCategories } from "../CatalogCategoriesPage";
import { useCategoriesOptionsFunctions } from "../hooks/useCategoriesOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import { DelayCategory } from "../models/CatalogCategory";
import { deleteCatalogCategory } from "../repositories/CatalogCategoriesRepository";
import { UpsertCategoryModal } from "./UpsertCategoryModal";
import { DeleteConfirmModal } from "@/controls/modals/DeleteConfirmModal";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
	LIST_HEADER_HEIGHT,
	LIST_ROW_HEIGHT,
	listColDef,
} from "@/controls/ag-grid/listGrid";
import {
	OneLineCellStyles,
	LIST_ROW_CLASS,
} from "@/controls/ag-grid/OneLineCells";

// A row click opens a modal, and modals stay out of the URL, so only the filter travels.
const URL_SCHEMA = {
	onlyActives: urlParam.bool(SHOW_ONLY_ACTIVES_DEFAULT),
};

export const TableCategories = ({
	categoryId,
}: {
	categoryId: string | undefined;
}) => {
	const [showModal, setShowModal] = useState<
		"create" | "update" | "delete" | ""
	>("");
	const [selectedRowData, setSelectedRowData] = useState<DelayCategory | null>(
		null,
	);
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [error, setError] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");
	const queryClient = useQueryClient();
	const deleteMutation = useMutation(deleteCatalogCategory, {
		onSuccess: async () => {
			await queryClient.invalidateQueries(["delayCategories"]);
			setNotificationMessage("The category was deleted successfully");
			setShowModal("");
		},
		onError: (error: Error) => setError(error.message),
	});
	const [{ onlyActives }, setFilters, { resetValues, isDefault }] =
		useUrlState(URL_SCHEMA);

	// Always read the whole catalog and narrow it here, so the checkbox does not
	// go back to the server on every click.
	const { data: rows, isLoading } = useCategories({
		includeInactives: true,
		onError: (error) => setError(error.message),
	});

	const visibleRows = useMemo(() => {
		const all = rows ?? [];
		return onlyActives ? all.filter((c) => c.IsActive) : all;
	}, [rows, onlyActives]);

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { getMenuOptions } = useCategoriesOptionsFunctions({
		setSelectedRowData,
		setShowModal,
	});
	const { columnDefs } = useTableData({
		setSelectedRowData,
		setShowModal,
		showContextMenu,
	});

	const { defaultColDef } = useGridDefinitions({});

	const onRowDoubleClicked = (event: any) => {
		setSelectedRowData(event.data);
		setShowModal("update");
	};

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;
			showContextMenu(event as any, data, "categoriesContext");
			if (data) {
				params.api.deselectAll();
				params.node?.setSelected(true);
			}
			return [];
		},
		[rows],
	);

	useEffect(() => {
		registerConfig({
			id: "categoriesContext",
			getOptions: getMenuOptions,
		});
	}, []);

	return (
		<>
			<Box
				sx={{
					width: "100%",
					height: "calc(100vh - 130px)",
					display: "flex",
					flexDirection: "column",
					gap: 1,
					p: 1,
				}}
			>
				<Typography variant="h5" sx={{ fontWeight: 600 }}>
					Stops Categories
				</Typography>

				<Paper elevation={3}>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1,
							flexWrap: "wrap",
							px: 1.5,
							py: 0.75,
						}}
					>
						<ShowOnlyActivesCheckbox
							checked={onlyActives}
							onChange={(value) => setFilters({ onlyActives: value })}
						/>
						<ResetFiltersButton
							onClick={() => resetValues(["onlyActives"])}
							disabled={isDefault("onlyActives")}
						/>

						<Box sx={{ flex: 1 }} />

						<Button
							variant="contained"
							color="primary"
							startIcon={<AddIcon />}
							onClick={() => setShowModal("create")}
							size="medium"
							sx={TOOLBAR_BUTTON_SX}
						>
							ADD CATEGORY
						</Button>
					</Box>
				</Paper>

				<Paper
					elevation={3}
					sx={{ flex: 1, minHeight: 0, mb: "5px", overflow: "hidden" }}
				>
					<OneLineCellStyles />
					<AgGridReact<DelayCategory>
						loading={isLoading}
						rowData={visibleRows}
						columnDefs={columnDefs}
						defaultColDef={listColDef(defaultColDef)}
						enableBrowserTooltips
						rowClass={LIST_ROW_CLASS}
						rowHeight={LIST_ROW_HEIGHT}
						headerHeight={LIST_HEADER_HEIGHT}
						animateRows
						getContextMenuItems={getContextMenuItems}
						onRowDoubleClicked={onRowDoubleClicked}
						onGridReady={(params) => setGridApi(params.api)}
						loadingOverlayComponent={CenteredLazyLoading}
						getRowId={(params) => `${params.data.DelayCategoryId}`}
						getRowClass={(params) =>
							params.data && !params.data.IsActive
								? "ag-row-inactive"
								: undefined
						}
						gridOptions={{
							rowSelection: {
								mode: "singleRow",
								checkboxes: false,
								enableClickSelection: true,
							},
						}}
					/>
				</Paper>
			</Box>
			<ErrorModal error={error} onHide={() => setError("")} />
			<UpsertCategoryModal
				delayCategory={selectedRowData}
				modal={
					showModal === "create" || showModal === "update" ? showModal : ""
				}
				onHide={() => setShowModal("")}
			/>
			<DeleteConfirmModal
				open={showModal === "delete"}
				title="DELETE STOP CATEGORY"
				itemName={selectedRowData?.DelayCategoryName ?? ""}
				isDeleting={deleteMutation.isLoading}
				onConfirm={() =>
					deleteMutation.mutate(selectedRowData?.DelayCategoryId ?? null)
				}
				onCancel={() => setShowModal("")}
			/>
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
		</>
	);
};
